import React, { useCallback, useMemo, useState } from 'react';
import {
  Modal,
  Steps,
  Button,
  message,
  Typography,
  Space,
  Tooltip,
  Table,
  Spin,
} from 'antd';
import { useTranslation } from 'react-i18next';
import FileUploader from '@components/FileUploader';
import { DownloadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import CsvExport from '@components/CsvExport/';
import { RcFile } from 'antd/lib/upload';
import { getModelData, getModelDataColumns } from './modelData';

import * as Styled from './styles.js';

const { Step } = Steps;
const { Text, Title, Paragraph } = Typography;

interface ISearchUploaderModal {
  visible: boolean;
  onClose: Function;
  setList: Function;
  columnName: string;
  description?: string;
  validation?: boolean;
  infoModalWidth?: number;
  alternateTitle?: string;
}

const SearchUploaderModal = ({
  visible,
  onClose,
  setList,
  columnName,
  description,
  infoModalWidth,
  alternateTitle,
  validation = true,
}: ISearchUploaderModal) => {
  const { t: translate } = useTranslation([
    'device-actions-modal',
    'device-actions',
  ]);

  const [current, setCurrent] = useState(0);
  const [uploadedFileRc, setUploadedFileRc] = useState<RcFile | undefined>(
    undefined,
  );
  const [uploadedFile, setUploadedFile] = useState();
  const [parsingFile, setParsingFile] = useState(false);
  const [uploadedDevices, setUploadedDevices] = useState<
    {
      [key: string]: string;
    }[]
  >();
  const [sendActionLoading, setSendActionLoading] = useState(false);

  const fileModelColumns = useMemo(
    () => getModelDataColumns(columnName?.toLowerCase()),
    [columnName],
  );

  const dataModelData = useMemo(
    () => getModelData(columnName?.toLowerCase()),
    [columnName],
  );

  const info = useCallback(() => {
    Modal.info({
      title: translate('csv-format-example'),
      width: infoModalWidth ?? 700,
      content: (
        <>
          <Table
            columns={fileModelColumns}
            dataSource={
              dataModelData as {
                key: string;
                msisdn?: string;
                imsi?: string;
                iccid?: string;
              }[]
            }
            pagination={false}
            size="small"
            tableLayout="auto"
            bordered
            style={{ margin: '12px 0' }}
          />
          <CsvExport
            columns={fileModelColumns}
            data={dataModelData}
            filename="model-import.csv"
            separator=","
          >
            <Button
              className="button-download"
              icon={<DownloadOutlined />}
              size="large"
              type="primary"
            >
              {translate('download-model')}
            </Button>
          </CsvExport>
        </>
      ),
    });
  }, [fileModelColumns, dataModelData, translate, infoModalWidth]);

  const uploadStep = useMemo(
    () => ({
      title: translate('steps.upload'),
      content: (
        <>
          <Space>
            <Text>
              {description ?? translate('select-devices')}
              <Tooltip placement="right" title={translate('help')}>
                <Button
                  icon={<QuestionCircleOutlined />}
                  size="small"
                  type="link"
                  onClick={info}
                />
              </Tooltip>
            </Text>
          </Space>
          <FileUploader
            uploadColumns={[
              { title: alternateTitle ?? columnName, dataIndex: columnName },
            ]}
            style={{ marginTop: 10 }}
            onUpload={setUploadedDevices}
            filesListState={[uploadedFile, setUploadedFile]}
            onRemove={() => setUploadedDevices([])}
          />
        </>
      ),
    }),
    [translate, description, info, alternateTitle, columnName, uploadedFile],
  );

  const validationStep = useMemo(
    () => ({
      title: translate('steps.validation'),
      content: (
        <Spin spinning={parsingFile} tip="Processing file">
          <div className="action-network-reset">
            <Title level={4}>
              {translate('selected-device', {
                count: uploadedDevices?.length,
              })}
            </Title>
            <Paragraph>
              {translate('search-upload-confirm', {
                field: columnName.toUpperCase(),
              })}
            </Paragraph>
          </div>
        </Spin>
      ),
    }),
    [columnName, parsingFile, translate, uploadedDevices?.length],
  );

  const clearStates = useCallback(() => {
    setUploadedDevices([]);
    setCurrent(0);
    setParsingFile(false);
    setUploadedFileRc(undefined);
    setUploadedFile(undefined);
  }, []);

  const steps = [uploadStep, validationStep];

  const handleConfirmAction = useCallback(async () => {
    setSendActionLoading(true);
    try {
      setList(uploadedDevices, uploadedFileRc);
    } catch {
      message.error({
        content: translate('upload-error'),
      });
    } finally {
      setSendActionLoading(false);
    }

    clearStates();
    onClose(false);
  }, [
    clearStates,
    onClose,
    setList,
    uploadedDevices,
    uploadedFileRc,
    translate,
  ]);

  const disabledButtonNext = useMemo(
    () => current === 0 && uploadedDevices?.length === 0,
    [current, uploadedDevices?.length],
  );

  const modalFooter = useMemo(
    () => (
      <div key="0" className="steps-action">
        {current > 0 && (
          <Button
            className="button-previous"
            onClick={() => setCurrent(current - 1)}
          >
            {translate('previous')}
          </Button>
        )}
        {current < steps.length - 1 && validation && (
          <Button
            disabled={disabledButtonNext}
            type="primary"
            onClick={() => setCurrent(current + 1)}
          >
            {translate('next')}
          </Button>
        )}
        {(current === steps.length - 1 || !validation) && (
          <Button
            disabled={parsingFile || disabledButtonNext}
            loading={sendActionLoading}
            type="primary"
            onClick={handleConfirmAction}
          >
            {translate('confirm')}
          </Button>
        )}
      </div>
    ),
    [
      current,
      handleConfirmAction,
      parsingFile,
      steps?.length,
      translate,
      sendActionLoading,
      disabledButtonNext,
      validation,
    ],
  );

  return (
    <Styled.ModalContainer
      footer={modalFooter}
      title={translate('search-upload', {
        field: alternateTitle ?? columnName.toUpperCase(),
      })}
      afterClose={() => document.body.style.removeProperty('overflow')}
      open={visible}
      onCancel={() => {
        clearStates();
        onClose(true);
      }}
      onOk={() => {
        clearStates();
        onClose(false);
      }}
      width="520px"
    >
      {validation && (
        <Steps current={current} size="small">
          {steps?.map(item => (
            <Step key={item.title} title={item.title} />
          ))}
        </Steps>
      )}
      <div className="steps-content">{steps[current].content}</div>
    </Styled.ModalContainer>
  );
};

export default SearchUploaderModal;
